import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MessageCircle, Mail, Instagram, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { BRAND, whatsappUrl, mailtoUrl } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.toasts.missingFields"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.toasts.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t("contact.toasts.failure"));
    } finally {
      setLoading(false);
    }
  };

  const talkMessage = t("common.defaultWhatsappMessage");
  const emailSubject = t("common.defaultEmailSubject");

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="py-24 md:py-32 max-w-7xl mx-auto px-6 lg:px-10"
    >
      <div className="grid lg:grid-cols-2 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="font-body uppercase tracking-[0.28em] text-xs text-[#C86A3D] font-semibold">
            {t("contact.eyebrow")}
          </span>
          <h2 className="font-display font-black tracking-tight text-4xl md:text-5xl text-[#1A1A1A] mt-4 leading-tight">
            <Trans
              i18nKey="contact.title"
              components={{
                em: (
                  <span className="font-serif-editorial italic font-normal text-[#2A3563]" />
                ),
              }}
            />
          </h2>
          <p className="font-body text-[#1A1A1A]/70 mt-6 max-w-md">
            {t("contact.description")}
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={whatsappUrl(talkMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-sm bg-[#25D366] flex items-center justify-center text-white">
                <MessageCircle size={22} />
              </span>
              <span>
                <span className="block font-body text-xs uppercase tracking-widest text-[#1A1A1A]/50">
                  {t("contact.channels.whatsapp")}
                </span>
                <span className="block font-display font-bold text-lg text-[#1A1A1A] group-hover:text-[#2A3563] transition-colors">
                  {BRAND.phoneDisplay}
                </span>
              </span>
            </a>

            <a
              href={mailtoUrl(emailSubject)}
              data-testid="contact-email-btn"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-sm bg-[#2A3563] flex items-center justify-center text-white">
                <Mail size={22} />
              </span>
              <span>
                <span className="block font-body text-xs uppercase tracking-widest text-[#1A1A1A]/50">
                  {t("contact.channels.email")}
                </span>
                <span className="block font-display font-bold text-lg text-[#1A1A1A] group-hover:text-[#2A3563] transition-colors">
                  {BRAND.email}
                </span>
              </span>
            </a>

            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram-btn"
              className="flex items-center gap-4 group"
            >
              <span className="w-12 h-12 rounded-sm bg-[#C86A3D] flex items-center justify-center text-white">
                <Instagram size={22} />
              </span>
              <span>
                <span className="block font-body text-xs uppercase tracking-widest text-[#1A1A1A]/50">
                  {t("contact.channels.instagram")}
                </span>
                <span className="block font-display font-bold text-lg text-[#1A1A1A] group-hover:text-[#2A3563] transition-colors">
                  @{BRAND.instagram}
                </span>
              </span>
            </a>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-white border border-[#E5E5E0] rounded-sm p-8 md:p-10"
        >
          <div className="space-y-5">
            <div>
              <label className="font-body text-sm font-medium text-[#1A1A1A] mb-2 block">
                {t("contact.form.name")}
              </label>
              <Input
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                placeholder={t("contact.form.namePlaceholder")}
                className="rounded-sm border-[#E5E5E0] focus-visible:ring-[#2A3563]"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-sm font-medium text-[#1A1A1A] mb-2 block">
                  {t("contact.form.email")}
                </label>
                <Input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder={t("contact.form.emailPlaceholder")}
                  className="rounded-sm border-[#E5E5E0] focus-visible:ring-[#2A3563]"
                />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-[#1A1A1A] mb-2 block">
                  {t("contact.form.phone")}
                </label>
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder={t("contact.form.phonePlaceholder")}
                  className="rounded-sm border-[#E5E5E0] focus-visible:ring-[#2A3563]"
                />
              </div>
            </div>
            <div>
              <label className="font-body text-sm font-medium text-[#1A1A1A] mb-2 block">
                {t("contact.form.message")}
              </label>
              <Textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder={t("contact.form.messagePlaceholder")}
                rows={5}
                className="rounded-sm border-[#E5E5E0] focus-visible:ring-[#2A3563] resize-none"
              />
            </div>
            <Button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={loading}
              className="w-full bg-[#2A3563] hover:bg-[#1f2749] text-white font-body font-semibold rounded-sm py-6 text-base"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />{" "}
                  {t("contact.form.sending")}
                </>
              ) : (
                <>
                  <Send size={18} /> {t("contact.form.submit")}
                </>
              )}
            </Button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
